import React, { useState, useRef, useCallback } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';

// =============================================================================
// 3D TILT CARD
// =============================================================================

interface TiltCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  tiltAmount?: number;
  glareEnabled?: boolean;
  scale?: number;
  perspective?: number;
}

export const TiltCard: React.FC<TiltCardProps> = ({
  children,
  className,
  style,
  tiltAmount = 10,
  glareEnabled = true,
  scale = 1.02,
  perspective = 1000,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);

  const rotateX = useTransform(y, [0, 1], [tiltAmount, -tiltAmount]);
  const rotateY = useTransform(x, [0, 1], [-tiltAmount, tiltAmount]);

  const springConfig = { stiffness: 300, damping: 30 };
  const springRotateX = useSpring(rotateX, springConfig);
  const springRotateY = useSpring(rotateY, springConfig);

  const glareX = useTransform(x, [0, 1], ['-50%', '150%']);
  const glareY = useTransform(y, [0, 1], ['-50%', '150%']);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const xPos = (e.clientX - rect.left) / rect.width;
      const yPos = (e.clientY - rect.top) / rect.height;

      x.set(xPos);
      y.set(yPos);
    },
    [x, y]
  );

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    x.set(0.5);
    y.set(0.5);
  }, [x, y]);

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        perspective,
        transformStyle: 'preserve-3d',
        ...style,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        style={{
          rotateX: springRotateX,
          rotateY: springRotateY,
          transformStyle: 'preserve-3d',
          position: 'relative',
          width: '100%',
          height: '100%',
        }}
        animate={{ scale: isHovered ? scale : 1 }}
        transition={{ duration: 0.2 }}
      >
        {children}

        {/* Glare effect */}
        {glareEnabled && (
          <motion.div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              style={{
                position: 'absolute',
                width: '200%',
                height: '200%',
                background:
                  'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 50%)',
                x: glareX,
                y: glareY,
                pointerEvents: 'none',
              }}
            />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// HOVER LIFT CARD
// =============================================================================

interface HoverLiftCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  liftAmount?: number;
  shadowIntensity?: 'low' | 'medium' | 'high';
  onClick?: () => void;
}

const shadowPresets = {
  low: {
    rest: '0 2px 8px rgba(0,0,0,0.1)',
    hover: '0 8px 20px rgba(0,0,0,0.15)',
  },
  medium: {
    rest: '0 4px 12px rgba(0,0,0,0.1)',
    hover: '0 12px 30px rgba(0,0,0,0.2)',
  },
  high: {
    rest: '0 4px 16px rgba(0,0,0,0.12)',
    hover: '0 20px 40px rgba(0,0,0,0.25)',
  },
};

export const HoverLiftCard: React.FC<HoverLiftCardProps> = ({
  children,
  className,
  style,
  liftAmount = 8,
  shadowIntensity = 'medium',
  onClick,
}) => {
  const shadows = shadowPresets[shadowIntensity];

  return (
    <motion.div
      className={className}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
      initial={{ y: 0, boxShadow: shadows.rest }}
      whileHover={{
        y: -liftAmount,
        boxShadow: shadows.hover,
      }}
      whileTap={onClick ? { scale: 0.98 } : {}}
      transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25,
      }}
      onClick={onClick}
    >
      {children}
    </motion.div>
  );
};

// =============================================================================
// FLIP CARD
// =============================================================================

interface FlipCardProps {
  front: React.ReactNode;
  back: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  flipOnHover?: boolean;
  isFlipped?: boolean;
  onFlip?: (flipped: boolean) => void;
}

export const FlipCard: React.FC<FlipCardProps> = ({
  front,
  back,
  className,
  style,
  flipOnHover = false,
  isFlipped: controlledFlipped,
  onFlip,
}) => {
  const [internalFlipped, setInternalFlipped] = useState(false);
  const isFlipped = controlledFlipped ?? internalFlipped;

  const handleClick = () => {
    if (!flipOnHover) {
      const newState = !isFlipped;
      setInternalFlipped(newState);
      onFlip?.(newState);
    }
  };

  return (
    <motion.div
      className={className}
      style={{
        perspective: 1000,
        cursor: flipOnHover ? 'default' : 'pointer',
        ...style,
      }}
      onHoverStart={flipOnHover ? () => setInternalFlipped(true) : undefined}
      onHoverEnd={flipOnHover ? () => setInternalFlipped(false) : undefined}
      onClick={handleClick}
    >
      <motion.div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
        }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
      >
        {/* Front */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
          }}
        >
          {front}
        </div>

        {/* Back */}
        <div
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {back}
        </div>
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// GLOW CARD
// =============================================================================

interface GlowCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  glowColor?: string;
  glowIntensity?: number;
}

export const GlowCard: React.FC<GlowCardProps> = ({
  children,
  className,
  style,
  glowColor = '#3b82f6',
  glowIntensity = 0.5,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow effect */}
      <motion.div
        style={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
          left: position.x - 150,
          top: position.y - 150,
          pointerEvents: 'none',
          filter: 'blur(40px)',
        }}
        animate={{ opacity: isHovered ? glowIntensity : 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </motion.div>
  );
};

// =============================================================================
// REVEAL CARD (Content reveals on hover)
// =============================================================================

interface RevealCardProps {
  children: React.ReactNode;
  overlay: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  revealDirection?: 'up' | 'down' | 'left' | 'right';
}

export const RevealCard: React.FC<RevealCardProps> = ({
  children,
  overlay,
  className,
  style,
  revealDirection = 'up',
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getOverlayAnimation = () => {
    const hiddenState = {
      up: { y: '100%' },
      down: { y: '-100%' },
      left: { x: '100%' },
      right: { x: '-100%' },
    };

    return {
      hidden: { ...hiddenState[revealDirection], opacity: 0.8 },
      visible: { x: 0, y: 0, opacity: 1 },
    };
  };

  const animation = getOverlayAnimation();

  return (
    <motion.div
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main content */}
      <motion.div
        animate={{
          scale: isHovered ? 1.05 : 1,
          filter: isHovered ? 'blur(2px)' : 'blur(0px)',
        }}
        transition={{ duration: 0.3 }}
      >
        {children}
      </motion.div>

      {/* Overlay */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.7)',
          backdropFilter: 'blur(4px)',
        }}
        initial="hidden"
        animate={isHovered ? 'visible' : 'hidden'}
        variants={animation}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {overlay}
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// PULSE CARD (Attention-grabbing)
// =============================================================================

interface PulseCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  pulseColor?: string;
  isActive?: boolean;
}

export const PulseCard: React.FC<PulseCardProps> = ({
  children,
  className,
  style,
  pulseColor = '#3b82f6',
  isActive = true,
}) => {
  return (
    <motion.div
      className={className}
      style={{
        position: 'relative',
        ...style,
      }}
    >
      {/* Pulse rings */}
      {isActive && (
        <>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                inset: -4,
                borderRadius: 'inherit',
                border: `2px solid ${pulseColor}`,
                pointerEvents: 'none',
              }}
              animate={{
                scale: [1, 1.1, 1.2],
                opacity: [0.6, 0.3, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.4,
                ease: 'easeOut',
              }}
            />
          ))}
        </>
      )}

      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </motion.div>
  );
};

export default {
  TiltCard,
  HoverLiftCard,
  FlipCard,
  GlowCard,
  RevealCard,
  PulseCard,
};
